// src/components/MapView/Legend.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDown, ChevronUp, Filter, X } from "lucide-react";

import { useTierConfig, useTierCounts } from "../../hooks";
import {
  toggleHighlightedTier,
  clearHighlightedTiers,
  selectHighlightedTiers,
} from "../../store/mapSlice";

/**
 * Legend component - displays tier colors and counts.
 * Allows filtering by clicking on tiers.
 * Starts collapsed below lg (1024px) for a cleaner initial view.
 */
const Legend = () => {
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 1024;
    }
    return false;
  });

  const { tierCutoffs } = useTierConfig();
  const { tiersWithCounts, totalCount, noDataCount } = useTierCounts();
  const highlightedTiers = useSelector(selectHighlightedTiers);

  const hasActiveFilter = highlightedTiers.length > 0;

  const handleTierClick = (normalizedKey) => {
    dispatch(toggleHighlightedTier(normalizedKey));
  };

  const handleClearFilters = () => {
    dispatch(clearHighlightedTiers());
  };

  const isTierActive = (normalizedKey) => {
    if (highlightedTiers.length === 0) return true;
    return highlightedTiers.includes(normalizedKey);
  };

  return (
    <div className="absolute bottom-20 left-3 lg:bottom-6 lg:left-4 z-[1000]">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden min-w-[160px] lg:min-w-[200px]">
        {/* Header */}
        <button
          className="flex items-center justify-between w-full px-2.5 lg:px-3 py-1.5 lg:py-2 bg-gray-50 border-b hover:bg-gray-100 transition"
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="flex items-center gap-1.5 lg:gap-2">
            <span className="text-xs lg:text-sm font-semibold text-gray-700">
              Legend
            </span>
            {hasActiveFilter && (
              <span className="flex items-center gap-1 text-xs text-secondary font-medium">
                <Filter size={10} />
                <span className="hidden lg:inline">Filtered</span>
              </span>
            )}
          </div>
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* Content */}
        {!collapsed && (
          <div className="p-2 lg:p-3">
            {hasActiveFilter && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-1.5 lg:mb-2 transition"
              >
                <X size={12} />
                Clear filters
              </button>
            )}

            <div className="space-y-0.5 lg:space-y-1">
              {tiersWithCounts.map((tier) => {
                const isActive = isTierActive(tier.normalizedKey);

                return (
                  <button
                    key={tier.key}
                    onClick={() => handleTierClick(tier.normalizedKey)}
                    className={`flex items-center justify-between w-full px-1.5 lg:px-2 py-1 lg:py-1.5 rounded transition ${
                      isActive
                        ? "bg-gray-50 hover:bg-gray-100"
                        : "opacity-40 hover:opacity-60"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 lg:gap-2">
                      <span
                        className="w-3.5 h-3.5 lg:w-4 lg:h-4 rounded-sm flex-shrink-0 border border-gray-200"
                        style={{ backgroundColor: tier.color }}
                      />
                      <span className="text-xs lg:text-sm text-gray-700">
                        {tier.label}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 font-medium tabular-nums ml-2">
                      {tier.count.toLocaleString()}
                    </span>
                  </button>
                );
              })}

              {noDataCount > 0 && tierCutoffs?.noData && (
                <div className="flex items-center justify-between px-1.5 lg:px-2 py-1 lg:py-1.5 opacity-60">
                  <div className="flex items-center gap-1.5 lg:gap-2">
                    <span
                      className="w-3.5 h-3.5 lg:w-4 lg:h-4 rounded-sm flex-shrink-0 border border-gray-200"
                      style={{ backgroundColor: tierCutoffs.noData.color }}
                    />
                    <span className="text-xs lg:text-sm text-gray-500">
                      {tierCutoffs.noData.label}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 font-medium tabular-nums ml-2">
                    {noDataCount.toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-2 lg:mt-3 pt-1.5 lg:pt-2 border-t flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Total</span>
              <span className="text-xs font-bold text-gray-700 tabular-nums">
                {totalCount.toLocaleString()}
              </span>
            </div>

            <p className="hidden lg:block mt-2 text-xs text-gray-400 italic">
              Click tiers to filter map
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Legend;
